// Employees tab (shift pickers on the Daily Ops Report use this roster).
// Part of the Reference page (components/Reference.jsx). Split out 2026-10-14.
import { useState, useMemo } from 'react';
import { useApi, putJson, delJson } from '../../api.js';

// ============================================================================
//  EmployeesPanel -- staff roster for the Daily Ops Report (2026-07-09).
//  Schema-031 backs this. Shift dropdowns on the daily report pull from
//  here as a multi-select chip picker of ACTIVE employees.
// ============================================================================
export default function EmployeesPanel({ canWrite = true }) {
  // Data: employee roster (used by the shift pickers on the Daily Ops Report) + filters + edit form.
  const { data, loading, error, refresh } = useApi('/api/employees');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [filter, setFilter]   = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const employees = (data && data.employees) || [];
  const tableMissing = data && data.tableExists === false;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return employees.filter(e => {
      if (!showInactive && !e.active) return false;
      if (!q) return true;
      return [e.initials, e.first_name, e.last_name, e.notes]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });
  }, [employees, filter, showInactive]);

  const startNew = () => setEditing({
    initials: '', first_name: '', last_name: '', active: true, notes: ''
  });
  const startEdit = (e) => setEditing({ ...e });

  // Actions: save / delete an employee.
  async function save() {
    if (!editing) return;
    setSaving(true);
    try { await putJson('/api/employees', editing); setEditing(null); refresh(); }
    catch (e) { alert('Save failed: ' + (e.message || e)); }
    finally { setSaving(false); }
  }
  async function remove(emp) {
    if (!confirm('Delete ' + emp.initials + '? Deactivate instead if they have historical shifts.')) return;
    setSaving(true);
    try { await delJson('/api/employees/' + emp.id); refresh(); }
    catch (e) { alert('Delete failed: ' + (e.message || e)); }
    finally { setSaving(false); }
  }

  if (loading && !data) return <div className="loading">Loading employees...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  // Screen: toolbar, roster table, add/edit pop-up.
  return (
    <div>
      {tableMissing && (
        <div style={{ background: '#fef2f2', border: '1px solid #dc2626', borderRadius: 6, padding: '0.6rem 0.9rem', marginBottom: '0.75rem', color: '#7f1d1d' }}>
          <strong>employees table missing.</strong> Run <code>db/schema-031-employees.sql</code>.
        </div>
      )}
      {/* Toolbar: filter, show inactive, + Add employee. */}
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <input type="text" placeholder="Filter by initials or name..." value={filter}
               onChange={e => setFilter(e.target.value)} style={{ flex: '1 1 240px', minWidth: 200 }} />
        <label className="small" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        {canWrite && (
          <button className="refresh-btn" onClick={startNew} style={{ background: '#0369a1' }}>+ Add employee</button>
        )}
      </div>

      {/* Roster table. */}
      <table className="fs-table tidy" style={{ fontSize: '0.92em', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 70 }}>Initials</th>
            <th>First name</th>
            <th>Last name</th>
            <th style={{ width: 80 }}>Active</th>
            <th>Notes</th>
            {canWrite && <th style={{ width: 120 }}></th>}
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && (
            <tr><td colSpan={canWrite ? 6 : 5} className="small" style={{ color: '#666', padding: '0.75rem' }}>
              No employees match.
            </td></tr>
          )}
          {filtered.map(emp => (
            <tr key={emp.id} style={emp.active ? undefined : { color: '#94a3b8' }}>
              <td><strong>{emp.initials}</strong></td>
              <td>{emp.first_name || ''}</td>
              <td>{emp.last_name || ''}</td>
              <td>{emp.active ? 'Yes' : 'No'}</td>
              <td className="small">{emp.notes || ''}</td>
              {canWrite && (
                <td>
                  <button className="link" onClick={() => startEdit(emp)} style={{ marginRight: '0.4rem' }}>Edit</button>
                  <button className="link" onClick={() => remove(emp)} style={{ color: '#991b1b' }}>Delete</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add / edit pop-up. */}
      {editing && (
        <div className="modal-backdrop" onClick={() => !saving && setEditing(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ minWidth: 320, maxWidth: 480 }}>
            <h3 style={{ marginTop: 0 }}>{editing.id ? 'Edit employee' : 'Add employee'}</h3>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <label className="small">Initials (unique among active)
                <input type="text" value={editing.initials}
                       onChange={e => setEditing({ ...editing, initials: e.target.value.toUpperCase() })}
                       maxLength={10} style={{ width: '100%', textTransform: 'uppercase', fontWeight: 600 }} />
              </label>
              <label className="small">First name
                <input type="text" value={editing.first_name || ''}
                       onChange={e => setEditing({ ...editing, first_name: e.target.value })}
                       style={{ width: '100%' }} />
              </label>
              <label className="small">Last name
                <input type="text" value={editing.last_name || ''}
                       onChange={e => setEditing({ ...editing, last_name: e.target.value })}
                       style={{ width: '100%' }} />
              </label>
              <label className="small" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input type="checkbox" checked={editing.active}
                       onChange={e => setEditing({ ...editing, active: e.target.checked })} />
                Active (uncheck to remove from shift dropdowns without losing history)
              </label>
              <label className="small">Notes
                <textarea value={editing.notes || ''}
                          onChange={e => setEditing({ ...editing, notes: e.target.value })}
                          rows={2} style={{ width: '100%' }} />
              </label>
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="refresh-btn" onClick={() => setEditing(null)} disabled={saving} style={{ background: '#888' }}>Cancel</button>
              <button className="refresh-btn" onClick={save} disabled={saving || !editing.initials.trim()} style={{ background: '#0369a1' }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
